import { useEffect, useState } from 'react';

import { SortDirection } from '@cast/design-system';
import { demoWoopWorkloadsList } from '@cast/fixtures';
import {
  OptimizationWorkload,
  WorkloadOptimizationState,
  WorkloadRuntime,
} from '@cast/types';

import { usePaginatedWorkloadsQuery } from 'hooks/queries/workload-optimization';

import { useIsWoopDemoMode } from './useIsWoopDemoMode';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type Args = {
  enabled?: boolean;
  pageLimit?: number;
  managementOptions?: WorkloadOptimizationState[];
  workloadIds?: string[];
  workloadNames?: string[];
  namespaces?: string[];
  scalingPolicyNames?: string[];
  kinds?: string[];
  sortField?: string;
  sortOrder?: SortDirection;
  anyContainerWithRuntime?: WorkloadRuntime;
};

export const usePaginatedWoopWorkloads = ({
  enabled = true,
  pageLimit,
  managementOptions,
  workloadIds,
  workloadNames,
  namespaces,
  scalingPolicyNames,
  kinds,
  sortField,
  sortOrder,
  anyContainerWithRuntime,
}: Args = {}) => {
  const { cluster } = useCluster();
  const isWoopDemoMode = useIsWoopDemoMode();
  const [demoWorkloads, setDemoWorkloads] = useState<OptimizationWorkload[]>();

  useEffect(() => {
    if (isWoopDemoMode) {
      demoWoopWorkloadsList().then((d) => setDemoWorkloads(d.workloads));
    }
  }, [isWoopDemoMode]);

  const queryResult = usePaginatedWorkloadsQuery({
    clusterId: cluster.id,
    enabled: !isWoopDemoMode && enabled,
    pageLimit,
    managementOptions,
    workloadIds,
    workloadNames,
    namespaces,
    scalingPolicyNames,
    kinds,
    sortField,
    sortOrder,
    anyContainerWithRuntime,
  });

  if (isWoopDemoMode) {
    return {
      workloads: demoWorkloads,
      isLoading: !demoWorkloads,
      error: undefined,
      refetch: () => {},
      hasNextPage: false,
      fetchNextPage: () => {},
      isFetching: false,
    };
  }

  return queryResult;
};
